import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { createRefetchContainer, graphql } from 'react-relay';
import { getUserType, getBuyerId } from 'dibs-cookie-jar';
import { addLoginHandlers } from 'dibs-buyer-layout/src/utils/loginHandlers';
import get from 'lodash.get';
import tracking from 'dibs-tracking';

import { HpRespInformationalContentRelayContainer } from './HpRespInformationalContent/HpRespInformationalContent';
import { HpRespHeroModule } from './HpRespHeroModule/HpRespHeroModule';
import { HpRespRecentlyViewedItems } from './HpRespRecentlyViewedItems/HpRespRecentlyViewedItems';
import { HpRespPersonalizationModule } from './HpRespPersonalizationModule/HpRespPersonalizationModule';
import { HpRespPromoModulePrimary } from './HpRespPromoModule/HpRespPromoModulePrimary';
import { HpRespCollectionsModule } from './HpRespCollectionsModule/HpRespCollectionsModule';
import { HpRespFeaturedModule } from './HpRespFeaturedModule/HpRespFeaturedModule';
import { HpRespPromoModuleSecondary } from './HpRespPromoModule/HpRespPromoModuleSecondary';
import { HpRespEditorialModule } from './HpRespEditorialModule/HpRespEditorialModule';
import { HpRespShopByModule } from './HpRespShopByModule/HpRespShopByModule';

import { HomePageContext } from './HomePageContext';
import { connectContext } from '../context/helpers/connectContext';

class HpRespLayoutComponent extends Component {
    state = {
        user: {
            /**
             * UserId is set only on client side because Personalizations modules
             * renders content only if userType and userId is provided
             */
            userId: null,

            /**
             * Set userType for server side rendering because we want to render
             * placeholders for Personalizations modules if user is logged in
             */
            userType: this.props.userType,
        },
    };

    componentDidMount() {
        this.setUserState(this.doRefetch);
        this.fireInitialTracking();

        addLoginHandlers(({ user }) => {
            if (user && user.isVerifiedTrade) {
                // keep this to make sure a trade member
                // logging in on the hp is "redirected"
                // to the trade homepage
                window.location.reload(true);
                return;
            }
            this.setUserState(this.doRefetch);
        });
    }

    doRefetch = ({ userId }) => {
        const { isDesktop } = this.props;

        this.props.relay.refetch({
            hasUserId: !!userId,
            userId: userId,
            numPersonalizationItems: isDesktop ? 5 : 4,
        });
    };

    setUserState(callback = () => {}) {
        const userId = getBuyerId(document.cookie);
        const userType = getUserType(document.cookie);

        if (userId) {
            this.setState(
                () => {
                    return {
                        user: {
                            userId,
                            userType,
                        },
                    };
                },
                () => callback({ userId })
            );
        }
    }

    fireInitialTracking() {
        tracking.trackPageview();
    }

    render() {
        const { relay, viewer } = this.props;
        const { homepage = {} } = viewer;

        const personalization = homepage.personalization || null;
        const { user } = this.state;

        return (
            <Fragment>
                <HpRespInformationalContentRelayContainer viewer={viewer} />
                <HpRespHeroModule homepage={homepage} />
                <HpRespRecentlyViewedItems user={get(viewer, 'user') || null} />
                <HpRespPersonalizationModule
                    personalization={personalization}
                    userId={user.userId}
                    userType={user.userType}
                />
                <HpRespPromoModulePrimary homepage={homepage} />
                <HpRespCollectionsModule homepage={homepage} />
                <HpRespFeaturedModule homepage={homepage} />
                <HpRespPromoModuleSecondary homepage={homepage} />
                <HpRespEditorialModule homepage={homepage} />
                <HpRespShopByModule homepage={homepage} />
            </Fragment>
        );
    }
}

HpRespLayoutComponent.propTypes = {
    relay: PropTypes.object.isRequired,
    viewer: PropTypes.object.isRequired,
    isDesktop: PropTypes.bool,
    userType: PropTypes.string,
};

function mapContextToProps({ isDesktop, userType }) {
    return {
        isDesktop,
        userType,
    };
}

const HpRespLayoutWithContext = connectContext(HomePageContext, mapContextToProps)(
    HpRespLayoutComponent
);

export const HpRespLayout = createRefetchContainer(
    HpRespLayoutWithContext,
    {
        viewer: graphql`
            fragment HpRespLayout_viewer on Viewer {
                homepage(rootId: $homepageId) {
                    ...HpRespHeroModule_homepage
                    ...HpRespPromoModulePrimary_homepage
                    ...HpRespCollectionsModule_homepage
                    ...HpRespFeaturedModule_homepage
                    ...HpRespPromoModuleSecondary_homepage
                    ...HpRespEditorialModule_homepage
                    ...HpRespShopByModule_homepage

                    personalization(userId: $userId, first: 4, numItems: $numPersonalizationItems)
                        @include(if: $hasUserId) {
                        ...HpRespPersonalizationModule_personalization
                    }
                }
                ...HpRespInformationalContent_viewer
                user(userId: $userId) @include(if: $hasUserId) {
                    ...HpRespRecentlyViewedItems_user
                }
            }
        `,
    },
    graphql`
        query HpRespLayoutRefetchQuery(
            $hasUserId: Boolean!
            $userId: String!
            $homepageId: String!
            $numPersonalizationItems: Int!
        ) {
            viewer {
                homepage(rootId: $homepageId) {
                    personalization(userId: $userId, first: 4, numItems: $numPersonalizationItems)
                        @include(if: $hasUserId) {
                        ...HpRespPersonalizationModule_personalization
                    }
                }
                user(userId: $userId) @include(if: $hasUserId) {
                    ...HpRespRecentlyViewedItems_user
                }
            }
        }
    `
);
